import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, AlertTriangle, BadgePercent, Wrench } from "lucide-react";
import { listNews } from "@/api/news";
import type { NewsListItem, NewsCategoria } from "@/types/news";
import { CategoryBadge } from "./CategoryBadge";

const ICONS: Record<NewsCategoria, React.ReactNode> = {
  Novita: <Newspaper className="w-6 h-6 text-brand" />,
  Promo: <BadgePercent className="w-6 h-6 text-green-600" />,
  Tecnico: <Wrench className="w-6 h-6 text-blue-600" />,
  Urgente: <AlertTriangle className="w-6 h-6 text-red-500" />,
};

const BG: Record<NewsCategoria, string> = {
  Novita: "bg-brand/10",
  Promo: "bg-green-100",
  Tecnico: "bg-blue-100",
  Urgente: "bg-red-100",
};

function formatShort(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
}

export function UltimeNovitaWidget() {
  const [items, setItems] = useState<NewsListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listNews({ page: 1, page_size: 3 })
      .then((r) => setItems(r.items))
      .catch(() => setError("Errore nel caricamento."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-semibold text-lg flex items-center gap-2">
          <Newspaper className="w-5 h-5 text-brand" /> Ultime novità
        </h2>
        <Link to="/news" className="text-sm text-brand font-medium hover:underline">
          Vedi tutte →
        </Link>
      </div>

      {loading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 bg-slate-100 animate-pulse rounded" />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="text-sm text-muted-foreground py-3">Errore nel caricamento.</div>
      )}
      {!loading && !error && items.length === 0 && (
        <div className="text-sm text-muted-foreground py-3">Nessuna novità.</div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="flex flex-col divide-y">
          {items.map((n) => (
            <Link key={n.id} to={`/news/${n.id}`} className="py-3 flex gap-3 hover:bg-slate-50">
              <div
                className={`w-14 h-14 rounded flex items-center justify-center shrink-0 ${BG[n.categoria]}`}
              >
                {ICONS[n.categoria]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {!n.letta && <span className="w-2 h-2 bg-brand rounded-full" />}
                  <CategoryBadge categoria={n.categoria} size="sm" />
                  <span className="text-xs text-muted-foreground">{formatShort(n.data_pubblicazione)}</span>
                </div>
                <div className="text-sm font-medium truncate">{n.titolo}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
